import { useMemo, useState, useCallback } from 'react';

import { CircleAlertIcon, InfoIcon, XIcon } from 'lucide-react';
import { useFieldArray, useFormContext, useWatch, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import Link from '@/components/atoms/Link/Link';
import { WhatsAppTemplateCombobox } from '@/components/atoms/WhatsAppTemplateCombobox';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  FormAutocomplete,
  AutocompleteOption,
} from '@/components/ui/custom/Autocomplete/Autocomplete';
import { FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import {
  getCampaignContextVariableOptions,
  CampaignForm,
  isVoiceIntegrationType,
  voiceSettingsSenderPath,
} from '@/modules/campaigns/utils';
import { senderArrayFieldPath, senderFieldPath } from '@/modules/campaigns/utils/senderFieldPath';
import { getUnresolvedSenderVariables } from '@/modules/campaigns/utils/utils';
import { WHATSAPP_MANAGER_URL } from '@/modules/whatsapp/constants';
import {
  extractVariableNames,
  getComponentsFromTemplate,
  getVariablesLength,
  isMediaHeader,
} from '@/modules/whatsapp/utils';
import { selectCampaignId } from '@/redux/session/selectors';
import { WhatsappTemplate } from '@/shared/types/broadcast';
import { IntegrationType } from '@/shared/types/integration';

import { useCampaignSubscribers } from '../../hooks/useCampaignSubscribers';
import { CampaignEditor } from '../CampaignEditor';
import { CampaignRichEditor } from '../CampaignRichEditor';
import { AnsweringMachineAction } from './AnsweringMachineAction';
import { HeaderMediaSection } from './HeaderMediaSection';
import { VoiceCallSettings } from './VoiceCallSettings';

// Character limits for different integration types
const VOICE_CHARACTER_LIMIT = 1600;
const SMS_CHARACTER_LIMIT = 160;
const EMAIL_SUBJECT_CHARACTER_LIMIT = 256;
const EMAIL_BODY_CHARACTER_LIMIT = 1600;
// Soft threshold past which we nudge users to shorten their voice message
const VOICE_LENGTH_WARNING_THRESHOLD = 240;

interface MessageAuthoringProps {
  integrationType: IntegrationType;
  content?: string;
  templateId?: string;
  templates?: WhatsappTemplate[];
  loadingTemplates?: boolean;
  onContentChange: (content: string) => void;
  onTemplateChange: (template: WhatsappTemplate) => void;
  label?: string;
  variableErrorMessage?: string;
  // Specify which sender's parameters to use (e.g., 'primarySender' or 'fallbackSenders.0')
  senderPath: string;
  // Callback when any input within this component is focused
  onFocus?: () => void;
  // Voice-only: selected sender's integration has AMD enabled (shows the voicemail action toggle)
  amdEnabled?: boolean;
}

/**
 * Reusable message authoring component for different integration types
 * @param integrationType - The type of integration (whatsapp, infobip-sms, etc.)
 * @param content - Current content value
 * @param templateId - Current template ID (for WhatsApp)
 * @param templates - Available WhatsApp templates
 * @param loadingTemplates - Loading state for templates
 * @param onContentChange - Callback for content changes
 * @param onTemplateChange - Callback for template changes
 */
export function MessageAuthoring({
  integrationType,
  content = '',
  templateId,
  templates = [],
  loadingTemplates = false,
  onContentChange,
  onTemplateChange,
  variableErrorMessage,
  senderPath,
  onFocus,
  amdEnabled = false,
}: MessageAuthoringProps) {
  const { t } = useTranslation();
  const { control, setValue, clearErrors } = useFormContext<CampaignForm>();
  const [open, setOpen] = useState(false);
  const [voiceWarningDismissed, setVoiceWarningDismissed] = useState(false);

  const campaignId = useSelector(selectCampaignId);
  const { campaign_subscribers } = useCampaignSubscribers(campaignId);
  const variableOptions = useMemo(
    () => getCampaignContextVariableOptions(campaign_subscribers),
    [campaign_subscribers]
  );

  const availableVars = useMemo(
    () =>
      new Set(
        variableOptions.filter((opt) => opt.type === 'available_variables').map((opt) => opt.label)
      ),
    [variableOptions]
  );

  const watchedSender = useWatch({
    control,
    name: senderFieldPath(senderPath),
  }) as CampaignForm['primarySender'];
  const unresolvedVarsMessage = useMemo(() => {
    if (!watchedSender?.integrationId || !campaign_subscribers?.length) {
      return undefined;
    }
    const unresolved = getUnresolvedSenderVariables(watchedSender, availableVars);
    if (unresolved.length === 0) {
      return undefined;
    }
    return t('campaigns.missing_variables', { 0: unresolved.join(', ') });
  }, [watchedSender, availableVars, campaign_subscribers, t]);

  const effectiveVariableError = variableErrorMessage || unresolvedVarsMessage;

  // The campaign-level opener block belongs to the first voice sender, not to
  // the primary, so it never hides while its values still reach a sender
  const [watchedPrimary, watchedFallbacks, fallbackIfNotDelivered, fallbackIfNoResponse] = useWatch(
    {
      control,
      name: ['primarySender', 'fallbackSenders', 'fallbackIfNotDelivered', 'fallbackIfNoResponse'],
    }
  );
  const ownsVoiceSettings =
    senderPath ===
    voiceSettingsSenderPath({
      primarySender: watchedPrimary,
      fallbackSenders: watchedFallbacks,
      fallbackIfNotDelivered,
      fallbackIfNoResponse,
    });

  // Set up field arrays for this specific sender's parameters
  const { fields: headerFields, append: headerAppend } = useFieldArray({
    control,
    name: senderArrayFieldPath(senderPath, 'headerParameters'),
  });

  const { fields: bodyFields, append: bodyAppend } = useFieldArray({
    control,
    name: senderArrayFieldPath(senderPath, 'bodyParameters'),
  });

  const { fields: footerFields, append: footerAppend } = useFieldArray({
    control,
    name: senderArrayFieldPath(senderPath, 'footerParameters'),
  });

  // Find the selected template
  const currentTemplate = templates.find((t) => t.id === templateId);

  // Get variable counts for the current template
  const { header, body, footer } = currentTemplate
    ? getComponentsFromTemplate(currentTemplate)
    : { header: null, body: null, footer: null };
  const headerVariablesCount = getVariablesLength(header);
  const bodyVariablesCount = getVariablesLength(body);
  const footerVariablesCount = getVariablesLength(footer);

  // Handle template change and create variable fields
  const handleTemplateChange = (newTemplateId: string) => {
    const template = templates.find((t) => t.id === newTemplateId);

    // Set the template in the form (undefined if not found or empty string)
    setValue(senderFieldPath(senderPath, 'template'), template || undefined);
    onTemplateChange(template);

    if (template) {
      // Clear existing parameters for this specific sender
      setValue(senderFieldPath(senderPath, 'headerParameters'), []);
      setValue(senderFieldPath(senderPath, 'bodyParameters'), []);
      setValue(senderFieldPath(senderPath, 'footerParameters'), []);
      clearErrors(senderFieldPath(senderPath, 'headerParameters'));
      clearErrors(senderFieldPath(senderPath, 'bodyParameters'));
      clearErrors(senderFieldPath(senderPath, 'footerParameters'));

      // Clear media header parameters
      setValue(senderFieldPath(senderPath, 'headerMediaParam.type'), '');
      setValue(senderFieldPath(senderPath, 'headerMediaParam.value'), '');
      clearErrors(senderFieldPath(senderPath, 'headerMediaParam.type'));
      clearErrors(senderFieldPath(senderPath, 'headerMediaParam.value'));

      const { header, body, footer } = getComponentsFromTemplate(template);

      // Create fields for header variables (only for text headers, not media headers)
      const headerVariables = getVariablesLength(header);
      if (headerVariables > 0 && !isMediaHeader(header)) {
        for (let i = 0; i < headerVariables; i++) {
          headerAppend({ name: '' });
        }
      }

      // Create fields for body variables
      const bodyVariables = getVariablesLength(body);
      if (bodyVariables > 0) {
        for (let i = 0; i < bodyVariables; i++) {
          bodyAppend({ name: '' });
        }
      }

      // Create fields for footer variables
      const footerVariables = getVariablesLength(footer);
      if (footerVariables > 0) {
        for (let i = 0; i < footerVariables; i++) {
          footerAppend({ name: '' });
        }
      }
    }
  };

  /**
   * Handle creating a new variable entry
   * For variables, the new value is used directly
   */
  const handleCreateNewVariable = useCallback((_newValue: string) => {
    // The FormAutocomplete already updates the form value via onChange
    // No additional handling needed for new entries
  }, []);

  /**
   * Transform options from Option[] to AutocompleteOption[]
   * Maps the 'type' field to 'category' with proper translation
   */
  const transformedOptions = useMemo((): AutocompleteOption[] => {
    return variableOptions.map((opt) => ({
      value: opt.value || '',
      label: opt.label || '',
      category: opt.type ? t(`auto_complete.group_labels.${opt.type}`) : undefined,
    }));
  }, [variableOptions, t]);

  /**
   * Renders a variable field with combobox
   */
  interface VariableFieldProps {
    field: { id: string; [key: string]: unknown };
    index: number;
    formName: string;
    variableNames: string[];
    template: WhatsappTemplate;
    options: AutocompleteOption[];
  }

  const VariableField = ({
    field,
    index,
    formName,
    variableNames,
    template,
    options,
  }: VariableFieldProps) => {
    // Get variable name based on parameter format
    const varDisplay =
      template.parameter_format === 'NAMED' && index < variableNames.length
        ? `{{${variableNames[index]}}}`
        : `{{${index + 1}}}`;

    return (
      <div key={field.id} className="flex w-full max-w-sm gap-10">
        <div className="flex h-10 w-20 items-center justify-start rounded-l text-sm">
          {varDisplay}
        </div>
        <div className="min-w-0 flex-1" onClick={onFocus} role="presentation">
          <FormAutocomplete<CampaignForm>
            control={control}
            options={options}
            name={`${formName}.${index}.name` as Path<CampaignForm>}
            placeholder={t('broadcasts.select_variable', { 0: varDisplay })}
            rules={{ required: t('validation.required') }}
            onCreateNew={handleCreateNewVariable}
          />
        </div>
      </div>
    );
  };

  /**
   * Renders variables section for a specific component (header, body, footer)
   */
  const VariablesSection = ({
    componentType,
    template,
  }: {
    componentType: 'header' | 'body' | 'footer';
    template: WhatsappTemplate;
  }) => {
    // Use the appropriate field array based on component type
    const fields =
      componentType === 'header'
        ? headerFields
        : componentType === 'body'
          ? bodyFields
          : footerFields;

    const variableNames = extractVariableNames(template, componentType);

    // Get the component to check if we should show variables
    const { header, body, footer } = getComponentsFromTemplate(template);
    const component =
      componentType === 'header' ? header : componentType === 'body' ? body : footer;

    // Don't show header variables for media headers
    if (componentType === 'header' && isMediaHeader(component)) {
      return null;
    }

    if (fields.length === 0) {
      return null;
    }

    return (
      <div className="mb-4">
        <p className="mb-2 text-sm leading-5 font-semibold">
          {t('try_it.add_variables', 'Add Variables')} -{' '}
          {componentType.charAt(0).toUpperCase() + componentType.slice(1)}
        </p>
        <p className="mb-3 text-sm leading-5 text-foreground-muted">
          {t('broadcasts.broadcast_variables')}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {fields.map((field, index) => (
            <VariableField
              key={field.id}
              field={field as { id: string; [key: string]: unknown }}
              index={index}
              formName={`${senderPath}.${componentType}Parameters`}
              variableNames={variableNames}
              template={template}
              options={transformedOptions}
            />
          ))}
        </div>
      </div>
    );
  };

  if (integrationType === 'whatsapp') {
    return (
      <div className="space-y-4">
        <FormField
          control={control}
          name={senderFieldPath(senderPath, 'template')}
          render={() => (
            <FormItem>
              <div className="space-y-2">
                <FormLabel className="text-sm font-medium">
                  {t('broadcasts.whatsapp_templates')}
                </FormLabel>
                <WhatsAppTemplateCombobox
                  templates={templates}
                  selectedTemplateId={templateId}
                  isLoading={loadingTemplates}
                  open={open}
                  onOpenChange={setOpen}
                  // Campaigns require a template; ignore the deselect (null) path.
                  onSelect={(id) => {
                    if (id) {
                      handleTemplateChange(id);
                    }
                  }}
                  onTriggerClick={onFocus}
                />
                <div className="text-sm text-foreground-muted">
                  <span>Available templates from your </span>
                  <Link href={WHATSAPP_MANAGER_URL} external>
                    WhatsApp manager
                  </Link>{' '}
                  account
                </div>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Variable Inputs Section */}
        {currentTemplate &&
          (headerVariablesCount > 0 ||
            bodyVariablesCount > 0 ||
            footerVariablesCount > 0 ||
            isMediaHeader(header)) && (
            <div className="space-y-4 rounded-lg border border-stroke bg-bg-muted p-4">
              {/* Show media header section for IMAGE, VIDEO, DOCUMENT (not LOCATION) */}
              {isMediaHeader(header) && header?.format !== 'LOCATION' && (
                <HeaderMediaSection
                  template={currentTemplate}
                  senderPath={senderPath}
                  variableOptions={variableOptions}
                  onFocus={onFocus}
                />
              )}
              {/* Only show sections that have variables */}
              {headerVariablesCount > 0 && !isMediaHeader(header) && (
                <VariablesSection componentType="header" template={currentTemplate} />
              )}
              {bodyVariablesCount > 0 && (
                <VariablesSection componentType="body" template={currentTemplate} />
              )}
              {footerVariablesCount > 0 && (
                <VariablesSection componentType="footer" template={currentTemplate} />
              )}
              {/* Variable error message */}
              {effectiveVariableError && (
                <p className="text-sm text-warning">{effectiveVariableError}</p>
              )}
            </div>
          )}
      </div>
    );
  }

  if (
    integrationType === 'infobip-sms' ||
    integrationType === 'infobip-rcs' ||
    integrationType === 'mstat-viber' ||
    integrationType === 'twilio-voice' ||
    integrationType === 'telnyx-voice' ||
    integrationType === 'agora-voice'
  ) {
    const isVoice = isVoiceIntegrationType(integrationType);
    const watchedContent = watchedSender?.content || content || '';

    // Let the warning reappear if the user dismisses it, shortens the message below the
    // threshold, then lengthens it past the threshold again.
    if (voiceWarningDismissed && watchedContent.length < VOICE_LENGTH_WARNING_THRESHOLD) {
      setVoiceWarningDismissed(false);
    }

    const getContentLabel = () => {
      if (integrationType === 'infobip-rcs') {
        return t('campaigns.build.rcs_content');
      }
      if (integrationType === 'mstat-viber') {
        return t('campaigns.build.viber_content');
      }
      if (isVoice) {
        return t('campaigns.build.initial_message');
      }
      return t('campaigns.build.sms_content');
    };
    const contentLabel = getContentLabel();
    const showVoiceLengthWarning =
      isVoice && watchedContent.length >= VOICE_LENGTH_WARNING_THRESHOLD && !voiceWarningDismissed;

    // Character limits: Voice has 1600 (enforced), SMS/RCS have 160 (not enforced)
    const characterLimit = isVoice ? VOICE_CHARACTER_LIMIT : SMS_CHARACTER_LIMIT;
    const enforceLimit = isVoice;

    return (
      <div className="space-y-4">
        <FormField
          control={control}
          name={senderFieldPath(senderPath, 'content')}
          render={() => (
            <FormItem>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <FormLabel className="text-sm font-medium">{contentLabel}</FormLabel>
                  <div className="flex items-center gap-1">
                    <InfoTooltip
                      content={
                        isVoice
                          ? t('broadcasts.voice_character_limit')
                          : t('broadcasts.messages_over_160_characters')
                      }
                      asChild={false}
                      size="large"
                      className="text-xs"
                    >
                      <div className="flex items-center gap-1 text-xs text-foreground-muted">
                        <span>
                          {watchedContent.length}/{characterLimit}
                        </span>
                        {!enforceLimit && <InfoIcon className="h-3 w-3" />}
                      </div>
                    </InfoTooltip>
                  </div>
                </div>

                <div
                  className="relative"
                  onClick={onFocus}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
                      onFocus?.();
                    }
                  }}
                  tabIndex={0}
                  role="button"
                >
                  <CampaignEditor
                    content={watchedContent}
                    onContentChange={onContentChange}
                    variableOptions={variableOptions}
                    maxLength={enforceLimit ? characterLimit : undefined}
                  />
                </div>

                <p className="text-xs text-foreground">{t('broadcasts.add_variables_by_typing')}</p>
                {showVoiceLengthWarning && (
                  <Alert
                    variant="warning"
                    className="mt-6! gap-y-1 rounded-md border-0 bg-bg-warning-soft pr-10 has-[>svg]:gap-x-2 [&>svg]:translate-y-0"
                  >
                    <CircleAlertIcon />
                    <AlertTitle>{t('campaigns.build.voice_message_long_title')}</AlertTitle>
                    <AlertDescription>
                      {t('campaigns.build.voice_message_long_description')}
                    </AlertDescription>
                    <Button
                      type="button"
                      variant="ghost-neutral"
                      size="icon-sm"
                      className="absolute top-2 right-2"
                      onClick={() => setVoiceWarningDismissed(true)}
                      aria-label={t('common.dismiss')}
                    >
                      <XIcon className="size-4" />
                    </Button>
                  </Alert>
                )}
                {effectiveVariableError && (
                  <p className="text-sm text-warning">{effectiveVariableError}</p>
                )}
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
        {isVoice && amdEnabled ? <AnsweringMachineAction senderPath={senderPath} /> : null}
        {/* Campaign-level, so it renders once: a copy per sender would edit the
            same shared fields side by side. It follows the first voice sender,
            so switching the primary to a non-voice channel moves the block to
            the voice fallback instead of hiding settings that still apply. */}
        {ownsVoiceSettings ? (
          <VoiceCallSettings senderPath={senderPath} amdEnabled={amdEnabled} />
        ) : null}
      </div>
    );
  }

  if (integrationType === IntegrationType.EMAIL) {
    // Watch the content and subject fields directly from the form to ensure they update properly
    const watchedContent = watchedSender?.content || content || '';
    const watchedSubject = watchedSender?.subject || '';

    return (
      <div className="space-y-4">
        <FormField
          control={control}
          name={senderFieldPath(senderPath, 'subject')}
          render={() => (
            <FormItem>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <FormLabel className="text-sm font-medium">
                    {t('campaigns.email_subject')}
                  </FormLabel>
                  <div className="text-xs text-foreground-muted">
                    {watchedSubject.length}/{EMAIL_SUBJECT_CHARACTER_LIMIT}
                  </div>
                </div>
                <div
                  className="relative"
                  onClick={onFocus}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
                      onFocus?.();
                    }
                  }}
                  tabIndex={0}
                  role="button"
                >
                  <CampaignEditor
                    content={watchedSubject}
                    onContentChange={(value) =>
                      setValue(senderFieldPath(senderPath, 'subject'), value)
                    }
                    variableOptions={variableOptions}
                    className="subject"
                    maxLength={EMAIL_SUBJECT_CHARACTER_LIMIT}
                  />
                </div>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name={senderFieldPath(senderPath, 'content')}
          render={() => (
            <FormItem>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <FormLabel className="text-sm font-medium">
                    {t('campaigns.email_content')}
                  </FormLabel>
                  <div className="text-xs text-foreground-muted">
                    {watchedContent.length}/{EMAIL_BODY_CHARACTER_LIMIT}
                  </div>
                </div>
                <div className="relative overflow-visible">
                  <CampaignRichEditor
                    content={watchedContent}
                    onContentChange={onContentChange}
                    variableOptions={variableOptions}
                    maxLength={EMAIL_BODY_CHARACTER_LIMIT}
                  />
                </div>

                <p className="text-xs text-foreground">{t('broadcasts.add_variables_by_typing')}</p>
                {effectiveVariableError && (
                  <p className="text-sm text-warning">{effectiveVariableError}</p>
                )}
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    );
  }

  // Fallback for other integration types
  return null;
}
